import * as React from "react"
import { createPortal } from "react-dom"

const DropdownPortalContext = React.createContext<
  React.RefObject<HTMLDivElement | null> | null
>(null)

/**
 * Hook: optional ref for the dropdown portal container.
 * When provided (e.g. by Dialog with dropdownSlotRef), Select content portals into this slot so it stays above the dialog.
 * When null, Select content portals to body with a high z-index layer.
 */
export function useDropdownPortalContainer(): React.RefObject<HTMLDivElement | null> | null {
  return React.useContext(DropdownPortalContext)
}

/**
 * Renders a global dropdown slot at document.body and provides its ref via context.
 * Apps should wrap the tree with this so Select/Popover content can portal above modals.
 * Modals can override by providing their own DropdownPortalContext.Provider with a modal-specific slot ref.
 */
export function DropdownPortalProvider({ children }: { children: React.ReactNode }) {
  const slotRef = React.useRef<HTMLDivElement | null>(null)

  const portalNode = React.useMemo(
    () => (
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none z-100"
      >
        <div ref={slotRef} id="dropdown-portal-slot" className="absolute inset-0 pointer-events-none" />
      </div>
    ),
    []
  )

  return (
    <>
      {typeof document !== "undefined" && document.body
        ? createPortal(portalNode, document.body)
        : null}
      <DropdownPortalContext.Provider value={slotRef}>
        {children}
      </DropdownPortalContext.Provider>
    </>
  )
}

export { DropdownPortalContext }
